import {
  WorkspaceDescriptor,
  workspaceMethods,
} from '@synapex/shared2/entities';
import { AdvertiserAccount } from '@synapex/shared2/gateway-rest-api';
import { maxBy } from 'lodash-es';

type AllReply = AdvertiserAccount.AllReply;
type CreatedReply = AdvertiserAccount.CreatedReply;

//user
export const activeUser = ({ user, technician }: AllReply) => ({
  ...user,
  technician,
});
export type ActiveUser = ReturnType<typeof activeUser>;

//account
const pickAdvertiserAccountFromAllReply = ({
  googleDriveConnection: __,
  ordAdditionalData: ___,
  permission: _,
  owner: ____,
  ...advertiserAccount
}: AllReply['advertiserAccounts'][number]) => advertiserAccount;
export type AdvertiserAccount = NonNullable<
  ReturnType<typeof pickAdvertiserAccountFromAllReply>
>;

const pickChannelRequestFromAllReply = (
  cr: AllReply['channelRequests'][number],
) => cr;
export type NewAccountChannelRequest = NonNullable<
  ReturnType<typeof pickChannelRequestFromAllReply>
>;

export const defaultAccount = ({
  advertiserAccounts,
}: AllReply): AdvertiserAccount | null => {
  if (advertiserAccounts.length === 0) {
    return null;
  }

  return pickAdvertiserAccountFromAllReply(advertiserAccounts[0]);
};

export const accountById = (
  accountId: number,
  { advertiserAccounts }: AllReply,
): AdvertiserAccount | null => {
  for (const acc of advertiserAccounts) {
    if (acc.advertiser_account_id === accountId) {
      return pickAdvertiserAccountFromAllReply(acc);
    }
  }

  return null;
};

export const accounts = ({
  advertiserAccounts,
}: AllReply): AdvertiserAccount[] =>
  advertiserAccounts.map(pickAdvertiserAccountFromAllReply);

export const newAccountChannelRequests = ({
  channelRequests,
}: AllReply): NewAccountChannelRequest[] =>
  channelRequests.map(pickChannelRequestFromAllReply);

//workspace

const pickWorkspaceFromAllReply = ({
  permission: _,
  googleDriveConnection: __,
  ordAdditionalData: ___,
  owner: _____,
  ...ws
}: AllReply['workspaces'][number]) => ws;

export type Workspace = NonNullable<
  ReturnType<typeof pickWorkspaceFromAllReply>
>;
export type Site = Extract<Workspace, { workspace_type: 'site' }>;
export type Channel = Extract<Workspace, { workspace_type: 'channel' }>;

const pickChannelRequestFromAllReplyAccount = (
  cr: AllReply['advertiserAccounts'][number]['channelRequests'][number],
) => cr;

export type ExistentAccountChannelRequest = ReturnType<
  typeof pickChannelRequestFromAllReplyAccount
>;

const pickWorkspaceFromAllReplyAccount = (
  ws: AllReply['advertiserAccounts'][number]['workspaces'][number],
): Workspace => ws;

export const defaultWorkspace = (
  account: AdvertiserAccount | null,
  { workspaces }: AllReply,
): Workspace | null => {
  if (!account) {
    return workspaces.length > 0
      ? pickWorkspaceFromAllReply(workspaces[0])
      : null;
  }

  return account.workspaces.length > 0
    ? pickWorkspaceFromAllReplyAccount(account.workspaces[0])
    : null;
};

export const workspaceById = (
  account: AdvertiserAccount | null,
  wd: WorkspaceDescriptor,
  { workspaces }: AllReply,
): Workspace | null => {
  if (account) {
    for (const ws of account.workspaces) {
      if (workspaceMethods.isSame(wd, ws)) {
        return pickWorkspaceFromAllReplyAccount(ws);
      }
    }

    return null;
  }

  for (const ws of workspaces) {
    if (workspaceMethods.isSame(wd, ws)) {
      return pickWorkspaceFromAllReply(ws);
    }
  }

  return null;
};

export const accountWorkspaces = (
  accountId: number | null,
  { advertiserAccounts, workspaces }: AllReply,
): Workspace[] => {
  if (accountId) {
    const account = advertiserAccounts.find(
      (aa) => aa.advertiser_account_id === accountId,
    );

    if (account) {
      return account.workspaces.map(pickWorkspaceFromAllReplyAccount);
    }
  }

  return workspaces.map(pickWorkspaceFromAllReply);
};

export const accountChannelRequests = (
  accountId: number | null,
  { advertiserAccounts }: AllReply,
): ExistentAccountChannelRequest[] => {
  if (accountId) {
    const account = advertiserAccounts.find(
      (aa) => aa.advertiser_account_id === accountId,
    );

    if (account) {
      return account.channelRequests.map(pickChannelRequestFromAllReplyAccount);
    }
  }

  return [];
};

//account and workspace

export const defaultAccountAndWorkspace = ({
  advertiserAccounts,
  workspaces,
}: AllReply): {
  account: AdvertiserAccount | null;
  workspace: Workspace | null;
} => {
  if (advertiserAccounts.length > 0) {
    const account = pickAdvertiserAccountFromAllReply(advertiserAccounts[0]);
    const workspace =
      account.workspaces.length > 0
        ? pickWorkspaceFromAllReplyAccount(account.workspaces[0])
        : null;
    return {
      account,
      workspace,
    };
  }

  return {
    account: null,
    workspace:
      workspaces.length > 0 ? pickWorkspaceFromAllReply(workspaces[0]) : null,
  };
};

// owner
const pickOwnerFromAccount = ({
  owner,
}: AllReply['advertiserAccounts'][number]) => owner;
export type Owner = ReturnType<typeof pickOwnerFromAccount>;

const pickOwnerFromWorkspace = ({
  owner,
}: AllReply['workspaces'][number]): Owner => owner;

// permission
const pickPermissionFromAccount = ({
  permission: { subscription, ...permission },
}: AllReply['advertiserAccounts'][number]) => ({ permission, subscription });
export type Permission = ReturnType<
  typeof pickPermissionFromAccount
>['permission'];
export type Subscription = ReturnType<
  typeof pickPermissionFromAccount
>['subscription'];
export type NormalSubscription = Exclude<Subscription, { status: 'superuser' }>;

const pickPermissionFromWorkspace = ({
  permission: { subscription, ...permission },
}: AllReply['workspaces'][number]) => ({ permission, subscription });

// ordData
const pickOrdDataFromAccount = ({
  ordAdditionalData,
  googleDriveConnection,
  workspaces,
}: AllReply['advertiserAccounts'][number]) => ({
  ordAdditionalData: ordAdditionalData
    ? {
        ...ordAdditionalData,
        lastKktu:
          maxBy(workspaces, ({ lastKktu }) => lastKktu?.created_at)?.lastKktu
            ?.kktu ?? null,
      }
    : null,
  googleDriveConnection,
});
export type OrdData = ReturnType<typeof pickOrdDataFromAccount>;

const pickOrdDataFromWorkspace = ({
  ordAdditionalData,
  googleDriveConnection,
  lastKktu,
}: AllReply['workspaces'][number]): OrdData => ({
  ordAdditionalData: ordAdditionalData
    ? {
        ...ordAdditionalData,
        lastKktu: lastKktu?.kktu ?? null,
      }
    : null,
  googleDriveConnection,
});

//MAIN, hooks up according to url pathname
export const pathParamsEntitiesById = (
  {
    advertiser_account_id,
    workspace_type,
    workspace_id,
    telegram_channel_id,
  }: {
    advertiser_account_id?: number;
    telegram_channel_id?: string;
  } & Partial<WorkspaceDescriptor>,
  reply: AllReply,
): {
  owner: Owner | null;
  newAccountChannelRequest: NewAccountChannelRequest | null;
  account: AdvertiserAccount | null;
  workspace: Workspace | null;
  channelRequest: ExistentAccountChannelRequest | null;
  permission: Permission | null;
  ordData: OrdData | null;
  subscription: Subscription | null;
} => {
  if (advertiser_account_id) {
    for (const aa of reply.advertiserAccounts) {
      if (aa.advertiser_account_id === advertiser_account_id) {
        const account = pickAdvertiserAccountFromAllReply(aa);
        const { permission, subscription } = pickPermissionFromAccount(aa);
        const ordData = pickOrdDataFromAccount(aa);
        const owner = pickOwnerFromAccount(aa);

        //channel-request and workspace are mutually exclusive
        if (telegram_channel_id) {
          for (const cr of account.channelRequests) {
            if (cr.telegram_channel_id === telegram_channel_id) {
              return {
                owner,
                newAccountChannelRequest: null,
                account,
                workspace: null,
                channelRequest: pickChannelRequestFromAllReplyAccount(cr),
                ordData,
                permission,
                subscription,
              };
            }
          }
        } else if (workspace_type) {
          if (workspace_id) {
            for (const accWs of account.workspaces) {
              if (
                workspaceMethods.isSame({ workspace_id, workspace_type }, accWs)
              ) {
                return {
                  owner,
                  newAccountChannelRequest: null,
                  account,
                  workspace: pickWorkspaceFromAllReplyAccount(accWs),
                  channelRequest: null,
                  ordData,
                  permission,
                  subscription,
                };
              }
            }
          } else {
            for (const accWs of account.workspaces) {
              //pick first of type if no workspace_id
              if (accWs.workspace_type === workspace_type) {
                return {
                  owner,
                  newAccountChannelRequest: null,
                  account,
                  workspace: pickWorkspaceFromAllReplyAccount(accWs),
                  channelRequest: null,
                  ordData,
                  permission,
                  subscription,
                };
              }
            }
          }
        }

        return {
          owner,
          newAccountChannelRequest: null,
          account,
          workspace: null,
          channelRequest: null,
          ordData,
          permission,
          subscription,
        };
      }
    }
  } else {
    //channel-request and workspace are mutually exclusive
    if (telegram_channel_id) {
      for (const cr of reply.channelRequests) {
        if (cr.telegram_channel_id === telegram_channel_id) {
          return {
            owner: null,
            newAccountChannelRequest: pickChannelRequestFromAllReply(cr),
            account: null,
            workspace: null,
            channelRequest: null,
            ordData: null,
            permission: null,
            subscription: null,
          };
        }
      }
    } else if (workspace_type) {
      if (workspace_id) {
        for (const ws of reply.workspaces) {
          if (workspaceMethods.isSame({ workspace_id, workspace_type }, ws)) {
            const { permission, subscription } =
              pickPermissionFromWorkspace(ws);
            const ordData = pickOrdDataFromWorkspace(ws);
            const owner = pickOwnerFromWorkspace(ws);

            return {
              owner,
              newAccountChannelRequest: null,
              account: null,
              workspace: pickWorkspaceFromAllReply(ws),
              channelRequest: null,
              ordData,
              permission,
              subscription,
            };
          }
        }
      } else {
        for (const ws of reply.workspaces) {
          if (ws.workspace_type === workspace_type) {
            const { permission, subscription } =
              pickPermissionFromWorkspace(ws);
            const ordData = pickOrdDataFromWorkspace(ws);
            const owner = pickOwnerFromWorkspace(ws);

            return {
              owner,
              newAccountChannelRequest: null,
              account: null,
              workspace: pickWorkspaceFromAllReply(ws),
              channelRequest: null,
              ordData,
              permission,
              subscription,
            };
          }
        }
      }
    }
  }

  return {
    owner: null,
    newAccountChannelRequest: null,
    account: null,
    workspace: null,
    channelRequest: null,
    permission: null,
    subscription: null,
    ordData: null,
  };
};

export const createdAccountAndInvite = ({
  invite,
  ...createdAccount
}: CreatedReply) => {
  return {
    invite,
    createdAccount: pickAdvertiserAccountFromAllReply(createdAccount),
  };
};
export type Invite = ReturnType<typeof createdAccountAndInvite>['invite'];
